import { useEffect } from "react";
import type { VideoMotion } from "@fiket/booth-schema";
import { MOTION_PICK_MIN } from "@fiket/booth-schema";
import { useRouter } from "../../store/router";
import { useI18n } from "../../i18n/useI18n";
import { motionName, useMotionsForStyle, useSelectedStyle } from "../../hooks/useStyles";
import { useVideoLoading } from "../../hooks/useVideoLoading";
import { useScrollHint } from "../../hooks/useScrollHint";
import { BackButton } from "../../components/ui/BackButton";
import { Button } from "../../components/ui/Button";
import { RingSpinner } from "../../components/ui/RingSpinner";
import { useBoothCaptionEnabled, useBoothPayment } from "../../store/booth";
import { anyPaymentActive } from "../Payment/paymentFlow";
import { afterMotionsRoute, videoBackRoute } from "./videoFlow";

/**
 * STAGE · VideoMotions (HERIT 캔버스 9.2, BOOTH-60 1b) — "동작을 골라 주세요 · 최대 두 개 · 고른 순서대로 이어집니다".
 * 2열 타일 그리드, 선택한 타일엔 순번 배지 ①②, 하단에 "SELECTED 1 A → 2 B". CTA 는 MOTION_PICK_MIN(1)개 이상 골라야 활성,
 * 상한 MOTION_PICK_MAX(2) 는 `toggleMotionPick` 이 막는다(2026-10-15: "두 개 필수" → "1~2개" 완화).
 * 다음 = 자막 입력(9.3, BOOTH-85) — 결제 분기는 자막 화면이 맡는다. 부스 자막 단계가 꺼져 있으면(`captionEnabled: false`) 자막을 건너뛰고
 * 이 화면이 생성 직전이라 결제 분기(`beginPayment(true)`)를 대신 맡는다. 뒤로 = 소개 화면. 동작 자체는 콘솔 "영상" 화면에서 관리하고 서버가 순서대로 프롬프트를 합성한다 — 여기선 id 만 고른다.
 * 타일 그림은 `MotionTileMedia` — 미리보기 영상(`previewVideoUrl`, BOOTH-91) 이 있으면 썸네일 대신 무음 루프, 없으면 썸네일.
 */
export function VideoMotionsView() {
  const go = useRouter((s) => s.go);
  const selected = useRouter((s) => s.selectedMotionIds);
  const toggleMotion = useRouter((s) => s.toggleMotion);
  const clearVideo = useRouter((s) => s.clearVideo);
  const beginPayment = useRouter((s) => s.beginPayment);
  const { t, language } = useI18n();
  const style = useSelectedStyle();
  const motions = useMotionsForStyle(style);
  const grid = useScrollHint<HTMLUListElement>(motions.length);
  const pay = useBoothPayment();
  const captionEnabled = useBoothCaptionEnabled();

  /** 다음 화면 — 자막 켬이면 자막 입력, 끔이면 결제(걸리면) 또는 생성 */
  const proceed = () => {
    const route = afterMotionsRoute(captionEnabled, anyPaymentActive(pay));
    if (route === "payment") beginPayment(true);
    go(route);
  };

  // 고를 동작이 하나도 없으면(콘솔에서 전부 비활성화 등) 이 화면은 의미가 없다 — 바로 다음 화면으로
  const noMotions = motions.length < MOTION_PICK_MIN;
  useEffect(() => {
    if (noMotions) proceed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noMotions]);

  const ready = selected.length >= MOTION_PICK_MIN;
  const picked = selected
    .map((id) => motions.find((m) => m.id === id))
    .filter((m): m is NonNullable<typeof m> => Boolean(m));

  return (
    <div className="view view--motions">
      <div className="view__head">
        <BackButton label={t.videoMotions.back} onClick={() => go(videoBackRoute())} />
        <h1 className="view__title">{t.videoMotions.title}</h1>
      </div>
      <p className="view__subtitle">{t.videoMotions.subtitle}</p>

      <div className="view__body">
        <ul ref={grid.ref} data-scroll={grid.hint} className="style-grid style-grid--motions no-scrollbar" role="listbox" aria-multiselectable aria-label={t.videoMotions.title}>
          {motions.map((m) => {
            const order = selected.indexOf(m.id);
            const active = order >= 0;
            const name = motionName(m, language);
            return (
              <li key={m.id}>
                <button
                  type="button"
                  className="tile tile--motion"
                  role="option"
                  aria-selected={active}
                  aria-pressed={active}
                  aria-label={name}
                  onClick={() => toggleMotion(m.id)}
                >
                  <div className={m.thumbnailUrl || m.previewVideoUrl ? "tile__img" : "tile__img tile__img--empty"}>
                    <MotionTileMedia motion={m} />
                    {active ? <span className="tile__order" aria-hidden>{order + 1}</span> : null}
                  </div>
                  <strong>{name}</strong>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="motion-selected" aria-live="polite">
        <span className="kicker">SELECTED</span>
        {picked.length > 0 ? (
          <span className="motion-selected__text">
            {picked.map((m, i) => (
              <span key={m.id}>
                {i > 0 ? <span className="motion-selected__arrow" aria-hidden> → </span> : null}
                <b>{i + 1}</b> {motionName(m, language)}
              </span>
            ))}
          </span>
        ) : (
          <span className="motion-selected__text motion-selected__text--hint">{t.videoMotions.hint}</span>
        )}
      </div>

      <div className="view__cta actions">
        <Button
          disabled={!ready}
          onClick={() => {
            clearVideo();
            proceed();
          }}
        >
          {t.videoMotions.next}
        </Button>
      </div>
    </div>
  );
}

/**
 * 동작 타일 그림 (BOOTH-91) — 운영자가 콘솔에서 만든 미리보기 영상이 있으면 `<video autoplay loop muted playsinline>`(poster = 썸네일,
 * 16:9 를 3:2 타일에 cover), 없으면 썸네일 `<img>`, 둘 다 없으면 빈 박스. 동작이 8개를 넘어 그리드가 스크롤될 때 화면 밖 영상은
 * IntersectionObserver 로 멈춰 iPad 디코더 부담을 줄인다(보이면 다시 재생).
 * 첫 프레임 전엔 포스터(썸네일) 위에 44px 링 스피너(순번 배지와 같은 크기) — `useVideoLoading`, BOOTH-93. 재버퍼엔 안 뜬다.
 */
function MotionTileMedia({ motion }: { motion: VideoMotion }) {
  const { loading, bind, videoRef } = useVideoLoading();
  useEffect(() => {
    const el = videoRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) void el.play().catch(() => {});
        else el.pause();
      },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
    // videoRef 는 안정적인 ref 객체 — 영상 URL 이 바뀌면(key 리마운트) 다시 관찰한다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motion.previewVideoUrl]);

  if (motion.previewVideoUrl) {
    return (
      <>
        <video
          key={motion.previewVideoUrl}
          {...bind}
          src={motion.previewVideoUrl}
          poster={motion.thumbnailUrl || undefined}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          disablePictureInPicture
          aria-hidden
        />
        {loading ? (
          <span className="tile__loading" aria-hidden>
            <RingSpinner size={44} />
          </span>
        ) : null}
      </>
    );
  }
  if (motion.thumbnailUrl) return <img src={motion.thumbnailUrl} alt="" draggable={false} />;
  return null;
}
